import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Settings, Search, ArrowUp } from "lucide-react";
import clsx from "clsx";
import FileUploadButton from "./FileUploadButton";
import toast from "react-hot-toast";
import {
  connectWebSocket,
  sendChatMessage,
  isWebSocketConnected,
} from "../services/websocketService";

const ChatInput = forwardRef(
  (
    { onSendMessage, onStartChat, onTitlesGenerated, engineType = "daily-news-analysis" },
    ref
  ) => {
    const [message, setMessage] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isResearchMode, setIsResearchMode] = useState(false);
    const textareaRef = useRef(null);
    const fileUploadRef = useRef(null);
    const dragCounterRef = useRef(0);
    const isProcessingRef = useRef(false);

    // WebSocket 연결 관리
    useEffect(() => {
      const initWebSocket = async () => {
        try {
          if (!isWebSocketConnected()) {
            console.log("WebSocket 연결 시도...");
            await connectWebSocket();
            setIsConnected(true);
            console.log("WebSocket 연결 성공!");
          } else {
            setIsConnected(true);
          }
        } catch (error) {
          console.error("WebSocket 연결 실패:", error);
          setIsConnected(false);
        }
      };

      initWebSocket();

      // 주기적으로 연결 상태 체크
      const interval = setInterval(() => {
        const connected = isWebSocketConnected();
        if (connected !== isConnected) {
          console.log(`WebSocket 연결 상태 변경: ${connected}`);
          setIsConnected(connected);
        }
      }, 3000);

      return () => {
        clearInterval(interval);
      };
    }, []);

    // 드래그 앤 드롭 이벤트 핸들러
    const handleDragEnter = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    }, []);

    const handleDragLeave = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    }, []);

    const handleDragOver = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    // 파일 처리 함수
    const handleDroppedFiles = async (files) => {
      if (files.length > 0) {
        for (const file of files) {
          if (
            file.type === "text/plain" ||
            file.name.endsWith(".txt") ||
            file.type === "application/pdf" ||
            file.name.endsWith(".pdf")
          ) {
            if (fileUploadRef.current && fileUploadRef.current.handleFile) {
              await fileUploadRef.current.handleFile(file);
            }
          } else {
            toast.error(`지원하지 않는 파일 형식: ${file.name}`, {
              duration: 4000,
              position: "top-center",
            });
          }
        }
      }
    };

    const handleDrop = useCallback(async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;

      const files = Array.from(e.dataTransfer.files);
      await handleDroppedFiles(files);
    }, []);

    useImperativeHandle(ref, () => ({
      handleDroppedFiles,
    }));

    // 파일 업로드 처리
    const handleFileContent = (content, fileInfo) => {
      console.log("파일 업로드됨:", fileInfo);

      const newFile = {
        id: Date.now() + Math.random(),
        fileName: fileInfo.fileName,
        fileType: fileInfo.fileType,
        fileSize: fileInfo.fileSize,
        pageCount: fileInfo.pageCount,
        content: content,
      };
      setUploadedFiles((prev) => [...prev, newFile]);

      toast.success(`파일 업로드 완료: ${fileInfo.fileName}`, {
        duration: 3000,
        position: "top-center",
      });
    };

    // 파일 제거
    const removeFile = (fileId) => {
      setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    };

    // 메시지 전송 핸들러
    const handleSubmit = async (e) => {
      if (e) e.preventDefault();

      // 디버깅 로그
      console.log("🔍 전송 버튼 클릭 - 상태 체크:", {
        message: message,
        messageTrimmed: message.trim(),
        messageLength: message.length,
        isTyping: isTyping,
        isLoading: isLoading,
        isConnected: isConnected,
        uploadedFilesCount: uploadedFiles.length,
        engineType: engineType,
        hasOnStartChat: !!onStartChat,
        hasOnSendMessage: !!onSendMessage,
        isProcessing: isProcessingRef.current,
      });

      // 이미 처리 중이면 무시
      if (isProcessingRef.current) {
        console.log("⚠️ 이미 메시지 처리 중, 중복 호출 방지");
        return;
      }

      // 로딩 중이면 무시
      if (isLoading) {
        console.log("⚠️ 로딩 중, 전송 불가");
        return;
      }

      // WebSocket 연결 체크
      if (!isConnected) {
        console.log("❌ 전송 실패: WebSocket 연결 안됨");
        toast.error("서버와 연결되지 않았습니다. 잠시 후 다시 시도해주세요.", {
          duration: 4000,
          position: "top-center",
        });
        
        // 재연결 시도
        try {
          await connectWebSocket();
          setIsConnected(true);
          toast.success("서버와 재연결되었습니다. 다시 시도해주세요.");
        } catch (error) {
          console.error("재연결 실패:", error);
        }
        return;
      }

      const messageText = message.trim();

      // 메시지나 파일이 있어야 전송 가능
      if (!messageText && uploadedFiles.length === 0) {
        console.log("❌ 전송 실패: 메시지와 파일 모두 없음");
        return;
      }

      console.log("✅ 전송 조건 충족, 메시지 전송 시작:", {
        hasMessage: !!messageText,
        hasFiles: uploadedFiles.length > 0
      });

      // 처리 시작 표시
      isProcessingRef.current = true;

      // 파일이 있으면 내용을 메시지에 추가
      let finalMessage = messageText;
      if (uploadedFiles.length > 0) {
        const fileContexts = uploadedFiles.map(file => 
          `[파일: ${file.fileName}]\n${file.content}`
        ).join("\n\n");
        finalMessage = messageText 
          ? `${messageText}\n\n${fileContexts}` 
          : fileContexts;
      }

      // onStartChat가 있으면 ChatPage로 네비게이션 (MainContent에서 사용)
      if (onStartChat) {
        console.log("🔀 ChatPage로 네비게이션 - 메시지:", finalMessage);
        // 상태 초기화
        setMessage("");
        setIsTyping(false);
        setUploadedFiles([]);
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
        
        onStartChat(finalMessage);
        
        // 플래그 리셋
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 1000);
        return;
      }

      // onSendMessage가 있으면 현재 페이지에서 처리 (ChatPage에서 사용)
      if (onSendMessage) {
        onSendMessage(finalMessage);
      }

      // ChatPage에서만 WebSocket으로 메시지 전송
      if (!onStartChat && isConnected) {
        setIsLoading(true);
        try {
          console.log(`${engineType} 엔진으로 메시지 전송:`, finalMessage);
          await sendChatMessage(finalMessage, engineType);
        } catch (error) {
          console.error("메시지 전송 실패:", error);
          toast.error("메시지 전송에 실패했습니다. 다시 시도해주세요.", {
            duration: 4000,
            position: "top-center",
          });
        } finally {
          setIsLoading(false);
        }
      }

      // 입력 초기화
      setMessage("");
      setUploadedFiles([]);
      setIsTyping(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      // 플래그 리셋
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 500);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    const handleInputChange = (e) => {
      const value = e.target.value;
      setMessage(value);
      setIsTyping(value.length > 0 || uploadedFiles.length > 0);

      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        const scrollHeight = textareaRef.current.scrollHeight;
        textareaRef.current.style.height = `${Math.min(scrollHeight, 400)}px`;
      }
    };

    // 파일 업로드 시 isTyping 상태 업데이트
    useEffect(() => {
      setIsTyping(message.length > 0 || uploadedFiles.length > 0);
    }, [uploadedFiles, message]);

    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, []);

    return (
      <fieldset
        className="flex w-full min-w-0 flex-col relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag Overlay */}
        {isDragging && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="relative">
              <div className="w-96 h-48 border-2 border-dashed border-blue-400 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 flex flex-col items-center justify-center gap-4 transition-all duration-200 animate-pulse">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center animate-bounce">
                    <svg
                      className="w-8 h-8 text-blue-600 dark:text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-1">
                      파일을 여기에 놓으세요
                    </h3>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      지원 형식: TXT, PDF (최대 50MB)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="!box-content flex flex-col bg-bg-000 mx-0 items-stretch transition-all duration-200 relative cursor-text z-10 rounded-2xl border border-border-300/15">
          <div className="flex flex-col gap-3.5 m-3.5">
            {/* Input Area */}
            <div className="relative">
              <div className="w-full font-large break-words transition-opacity duration-200 min-h-[1.5rem]">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isConnected
                      ? "오늘 어떤 도움을 드릴까요?"
                      : "서버 연결 중..."
                  }
                  className="w-full min-h-[1.5rem] max-h-[400px] overflow-y-auto resize-none bg-transparent border-none outline-none text-text-100 placeholder-text-500 font-large leading-relaxed scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent"
                  rows={1}
                  disabled={!isConnected || isLoading}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex gap-2.5 w-full items-center">
              <div className="relative flex-1 flex items-center gap-2 shrink min-w-0">
                {/* File Upload Button */}
                <div className="relative shrink-0">
                  <FileUploadButton
                    ref={fileUploadRef}
                    onFileContent={handleFileContent}
                    disabled={!isConnected || isLoading}
                  />
                </div>

                <div className="relative shrink-0">
                  <div className="flex items-center">
                    <button
                      className="claude-button group"
                      type="button"
                      onClick={() => {
                        setIsResearchMode(!isResearchMode);
                        toast.success(
                          isResearchMode
                            ? "연구 모드 비활성화"
                            : "연구 모드 활성화 - AI가 더 체계적으로 생각합니다",
                          { duration: 2000 }
                        );
                      }}
                      title={
                        isResearchMode
                          ? "연구 모드 활성화됨"
                          : "연구 모드 비활성화됨"
                      }
                    >
                      <Search size={16} />
                      <p className="min-w-0 pl-1 text-xs tracking-tight text-ellipsis whitespace-nowrap break-words overflow-hidden shrink">
                        연구
                      </p>
                    </button>
                  </div>
                </div>
              </div>

              {/* Connection Status Indicator */}
              <div className="flex items-center gap-1">
                <div
                  className={clsx(
                    "w-2 h-2 rounded-full",
                    isConnected ? "bg-green-500" : "bg-red-500 animate-pulse"
                  )}
                  title={isConnected ? "연결됨" : "연결 끊김"}
                />
              </div>

              {/* Send Button */}
              <div className="opacity-100">
                <button
                  className={clsx(
                    "inline-flex items-center justify-center relative shrink-0 select-none transition-colors h-8 w-8 rounded-md active:scale-95 !rounded-lg !h-8 !w-8",
                    isLoading
                      ? "bg-accent-main-100 text-white cursor-wait"
                      : (isTyping || uploadedFiles.length > 0) && isConnected
                      ? "bg-accent-main-000 text-white hover:bg-accent-main-200"
                      : "bg-gray-600 text-gray-400 cursor-not-allowed"
                  )}
                  disabled={(!isTyping && uploadedFiles.length === 0) || isLoading || !isConnected}
                  type="button"
                  onClick={handleSubmit}
                  aria-label="메시지 보내기"
                  title={
                    !isConnected
                      ? "서버 연결 중..."
                      : isLoading
                      ? "전송 중..."
                      : !isTyping && uploadedFiles.length === 0
                      ? "메시지를 입력하세요"
                      : "메시지 보내기 (Enter)"
                  }
                >
                  {isLoading ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <ArrowUp size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* File Preview Section */}
          {uploadedFiles.length > 0 && (
            <div className="overflow-hidden rounded-b-2xl">
              <div className="border-border-300/25 border-t-0.5 rounded-b-2xl bg-bg-100 !p-3.5 !m-0 flex flex-row overflow-x-auto gap-3 px-3.5 py-2.5 -my-1">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="relative">
                    <div className="group/thumbnail">
                      <div
                        className="rounded-lg text-left block cursor-pointer font-ui transition-all rounded-lg border-0.5 border-border-300/25 flex flex-col justify-between gap-2.5 overflow-hidden px-2.5 py-2 bg-bg-000 hover:border-border-200/50 hover:shadow-always-black/10 shadow-sm shadow-always-black/5"
                        style={{
                          width: "120px",
                          height: "120px",
                          minWidth: "120px",
                        }}
                      >
                        <div className="relative flex flex-col gap-1 min-h-0">
                          <h3 className="text-[12px] tracking-tighter break-words text-text-100 line-clamp-3">
                            {file.fileName}
                          </h3>
                          <p className="text-[10px] line-clamp-1 tracking-tighter break-words text-text-500">
                            {file.pageCount
                              ? `${file.pageCount}페이지`
                              : `${Math.ceil(file.fileSize / 1024)}KB`}
                          </p>
                        </div>
                        {/* Remove button */}
                        <button
                          onClick={() => removeFile(file.id)}
                          className="absolute top-1 right-1 p-1 rounded-full bg-bg-000/80 hover:bg-bg-100 transition-colors"
                          title="파일 제거"
                        >
                          <svg
                            className="w-3 h-3 text-text-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </fieldset>
    );
  }
);

ChatInput.displayName = "ChatInput";

export default ChatInput;